/* ============================================================================
   cookie-hunt-game.js — The Ben's Cookie Hunt main engine (Shopify edition).

   Differences from the standalone build:
   • assetUrl(path) resolves filenames through window.__GAME_ASSETS__ (injected
     by the Liquid section) so all images are served from the Shopify CDN.
   • STORE seed falls back to window.__GAME_CONFIG__.store (set in the section
     schema) before the hardcoded 'demo' string.
   • Boot is triggered with a readyState guard so it works whether
     DOMContentLoaded has already fired or not (needed for Liquid injection).
   ========================================================================= */
(() => {
'use strict';

/* ---------------------------------------------------------------------------
   Shopify CDN asset resolver.
   window.__GAME_ASSETS__ is a plain object { 'filename.ext': 'https://cdn…' }
   injected by the section's <script> block before this file loads.
   Falls back gracefully to the original path so standalone dev still works.
*/
function assetUrl(path){
  const map = window.__GAME_ASSETS__;
  if(map){
    const key = path.replace(/^.*\//, '');   // strip any directory prefix
    if(map[key]) return map[key];
  }
  return path;
}

const CFG = {
  DURATION: 60,
  SPAWN_MIN: 1.7, SPAWN_MAX: 3.5, MAX_LIVE: 5,
  TTL: 15, FADE: 0.6,

  HFOV: 62,
  CAM_FOV: 65,

  Z_FAR: 4.1, Z_NEAR: 1.5, Z_CATCH: 1.91,
  APPROACH: 2.1, RECEDE: 0.7,
  REF: 0.56,

  SPAWN_YAW: 75,
  PITCH_MIN: -18, PITCH_MAX: 34,
  MIN_SEP: 14,

  GYRO_WAIT_MS: 1200,
};

const FLAVOURS = window.__FLAVOURS__ || [];
const params   = new URLSearchParams(location.search);
const cfg      = window.__GAME_CONFIG__ || {};
const STORE    = params.get('store') || cfg.store || 'demo';
if(params.get('fov')) CFG.CAM_FOV = parseFloat(params.get('fov')) || CFG.CAM_FOV;

const clamp = (v,a,b)=>v<a?a:v>b?b:v;
const lerp  = (a,b,t)=>a+(b-a)*t;
const rand  = (r,a,b)=>a+(b-a)*r();
const rnd   = (a,b)=>a+(b-a)*Math.random();
const FONT  = "'Gill Sans','Gill Sans MT','Gill Sans Nova',Calibri,'Trebuchet MS',sans-serif";
const BRAND_RED = '#FA0A11';

function mulberry32(seed){ let s=seed>>>0; return function(){
  s|=0; s=s+0x6D2B79F5|0; let t=Math.imul(s^s>>>15,1|s);
  t=t+Math.imul(t^t>>>7,61|t)^t; return ((t^t>>>14)>>>0)/4294967296;};}
function hash(s){ let h=2166136261; for(let i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619);} return h>>>0;}

const video    = DOM.cam,  canvas = DOM.game,  ctx = canvas.getContext('2d');
const screens  = { start:DOM.start, over:DOM.over, selfie:DOM.selfie, unsupported:DOM.unsupported };
const hud      = DOM.hud;
const timerFill=DOM.timerfill, scoreEl=DOM.score, liveEl=DOM.live, clockEl=DOM.clock;
const countEl  = DOM.count,    toastEl=DOM.toast;
const selfCanvas = DOM.selfcanvas;
const selfCtx    = selfCanvas ? selfCanvas.getContext('2d') : null;

let DPR=1, W=0, H=0, minDim=0;
let state='start';
let prng = mulberry32(hash(STORE) ^ (Date.now()>>>0));
if(params.get('seed')) prng = mulberry32(hash(params.get('seed')));
let camOn=false, uid=0;
let cookies=[], particles=[], pops=[];
let score=0, tally={}, totalCaught=0;
let gameStart=0, lastT=0, nextSpawn=0, rafId=0;
const sprites = {};
const frame   = {};
let activeId = null;
let audioCtx = null;
let selfieFlav = null;
let selfiePos  = { x:0.5, y:0.50, s:1 };
let selfDragging = false;

const TUT = {
  TOTAL: 5.0,
  T_SPOT:  1.0,
  T_AIM:   2.4,
  T_FOCUS: 3.6,
  T_TAP:   4.4,
};
let tutStart = 0;
let tutFlav  = null;
let tutTapDone = false;
let tutGoShown = false;
let tutLastLabel = '';

/* ---------------------------------------------------------------------------
   On-screen angular gain.
*/
function ppdDeg(){
  const vw = video.videoWidth, vh = video.videoHeight;
  if(camOn && vw && vh){
    const scale = Math.max(W/vw, H/vh);
    return scale * Math.max(vw, vh) / CFG.CAM_FOV;
  }
  return W / CFG.HFOV;
}
const focalPx = () => ppdDeg() * 180/Math.PI;

/* ---------------------------------------------------------------------------
   Asset loading — all paths go through assetUrl() for Shopify CDN resolution.
*/
function loadImage(src){
  return new Promise(res => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = src;
  });
}
async function loadSprites(){
  await Promise.all(FLAVOURS.map(async f => {
    const im = await loadImage(assetUrl(f.sprite));
    if(!im){ sprites[f.id] = null; return; }
    const w=im.naturalWidth, h=im.naturalHeight;
    const sharp=document.createElement('canvas'); sharp.width=w; sharp.height=h;
    sharp.getContext('2d').drawImage(im,0,0);
    const blur =document.createElement('canvas'); blur.width=w;  blur.height=h;
    const bc=blur.getContext('2d'); bc.filter='blur(7px)'; bc.drawImage(im,0,0);
    sprites[f.id] = { sharp, blur, w, h };
  }));
}
async function loadFrame(){
  const list = ['heart','clapper','crumbs','bag'];
  await Promise.all(list.map(async n => {
    frame[n] = await loadImage(assetUrl('assets/frame/' + n + '.webp'));
  }));
}

/* ---- canvas sizing -------------------------------------------------------- */
function resize(){
  const vv = window.visualViewport;
  W = Math.round(vv ? vv.width : window.innerWidth);
  H = Math.round(vv ? vv.height : window.innerHeight);
  DPR = Math.min(window.devicePixelRatio || 1, 2.5);
  canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
  canvas.width  = Math.round(W * DPR);
  canvas.height = Math.round(H * DPR);
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  minDim = Math.min(W, H);
}

/* ---- camera --------------------------------------------------------------- */
async function startCamera(facing){
  if(!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('no-gum');
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: facing==='user' ? 'user' : { ideal:'environment' } },
    audio: false
  });
  stopCamera();
  video.srcObject = stream;
  video.setAttribute('playsinline',''); video.muted = true;
  await video.play().catch(()=>{});
  video.classList.toggle('mirror', facing==='user');
  video.style.display = 'block';
  camOn = true;
  return stream;
}
function stopCamera(){
  const s = video.srcObject;
  if(s){ s.getTracks().forEach(t=>t.stop()); video.srcObject = null; }
  camOn = false;
}

/* ---- audio (capture crunch) ---------------------------------------------- */
function unlockAudio(){
  try{
    audioCtx = audioCtx || new (window.AudioContext||window.webkitAudioContext)();
    if(audioCtx.state === 'suspended') audioCtx.resume();
  }catch(_){}
}
function crunch(){
  if(!audioCtx) return;
  const t = audioCtx.currentTime;
  const o = audioCtx.createOscillator(), g = audioCtx.createGain();
  o.type = 'triangle';
  o.frequency.setValueAtTime(190, t);
  o.frequency.exponentialRampToValueAtTime(70, t + 0.12);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(0.2, t + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0008, t + 0.18);
  o.connect(g).connect(audioCtx.destination);
  o.start(t); o.stop(t + 0.2);
}

/* ---- spawn / catch ------------------------------------------------------- */
function pickFlavour(){
  const tot = FLAVOURS.reduce((s,f)=>s+f.w, 0);
  let r = prng()*tot;
  for(const f of FLAVOURS){ if((r -= f.w) <= 0) return f; }
  return FLAVOURS[FLAVOURS.length-1];
}
function spawn(){
  if(cookies.length >= CFG.MAX_LIVE) return;
  if(!Look.calibrated()) return;
  const minSepRad = CFG.MIN_SEP * Math.PI / 180;
  let vW = null, ok = false;
  for(let i=0; i<8 && !ok; i++){
    const dY = rand(prng, -CFG.SPAWN_YAW, CFG.SPAWN_YAW);
    const dP = rand(prng,  CFG.PITCH_MIN,  CFG.PITCH_MAX);
    vW = Look.spawnVector(dY, dP);
    ok = cookies.every(c => Look.angBetween(c.vWorld, vW) > minSepRad);
  }
  const f = pickFlavour();
  cookies.push({
    _uid: ++uid, id: f.id, flav: f, vWorld: vW,
    z: CFG.Z_FAR, born: performance.now()/1000,
    alpha: 1, dying: false, deadAt: 0,
    spin: rand(prng,-1,1)*0.15, bob: rand(prng,0,Math.PI*2)
  });
}

function tryCatch(){
  if(state !== 'play' || !activeId) return;
  const c = cookies.find(x => x._uid === activeId);
  if(!c || c.z > CFG.Z_CATCH || c.dying) return;
  score += c.flav.p; totalCaught++;
  tally[c.id] = (tally[c.id] || 0) + 1;
  crunch();
  if(navigator.vibrate) navigator.vibrate(30);
  const sx = c._sx, sy = c._sy;
  const base = c.flav.colours.base, dk = c.flav.colours.dark;
  for(let i=0; i<18; i++){
    const a = Math.random()*Math.PI*2, v = rnd(60, 260);
    particles.push({
      x:sx, y:sy, vx:Math.cos(a)*v, vy:Math.sin(a)*v - 60,
      life:1, r:rnd(3,8), col: Math.random()<0.5 ? base : dk
    });
  }
  pops.push({ x:sx, y:sy, life:1, pts:'+'+c.flav.p, name:c.flav.name, col:BRAND_RED });
  particles.push({ ring:true, x:sx, y:sy, life:1, r:c._size*0.5, col:BRAND_RED });
  cookies = cookies.filter(x => x !== c);
  activeId = null;
}

function updateCookies(t, dt){
  const cx = W/2, cy = H/2, f = focalPx();
  let best = null, bestD = 1e9;
  for(const c of cookies){
    const p = Look.project(c.vWorld, f, W, H);
    c._sx = p.x; c._sy = p.y; c._on = p.on;
    if(p.on && !c.dying){
      const d = Math.hypot(c._sx - cx, c._sy - cy);
      if(d < bestD && d < minDim*0.16){ bestD = d; best = c; }
    }
    const age = t - c.born;
    if(!c.dying && age > CFG.TTL){ c.dying = true; c.deadAt = t; }
    if(c.dying){ c.alpha = clamp(1 - (t - c.deadAt)/CFG.FADE, 0, 1); }
  }
  activeId = best ? best._uid : null;
  for(const c of cookies){
    const focused = (c._uid === activeId);
    if(focused) c.z = Math.max(CFG.Z_NEAR, c.z - CFG.APPROACH * dt);
    else        c.z = Math.min(CFG.Z_FAR,  c.z + CFG.RECEDE   * dt);
  }
  cookies = cookies.filter(c => !(c.dying && c.alpha <= 0));
}

function cookieGeom(c){
  const size = (minDim * CFG.REF) / c.z;
  const focus = clamp((CFG.Z_FAR - c.z)/(CFG.Z_FAR - CFG.Z_NEAR), 0, 1);
  return { size, focus };
}

/* ---- rendering ----------------------------------------------------------- */
function drawCookies(t){
  const list = [...cookies].sort((a,b)=> b.z - a.z);
  for(const c of list){
    if(!c._on) continue;
    const sp = sprites[c.id];
    const g = cookieGeom(c); c._size = g.size;
    const bob = Math.sin(t*1.6 + c.bob) * g.size * 0.012;
    ctx.save();
    ctx.globalAlpha = c.alpha;
    ctx.translate(c._sx, c._sy + bob);
    ctx.rotate(Math.sin(t*0.6 + c.bob) * 0.04 * c.spin * 4);

    if(!sp){
      const r = g.size * 0.42;
      ctx.save();
      ctx.globalAlpha = c.alpha * 0.20 * g.focus;
      ctx.fillStyle = '#000';
      ctx.beginPath(); ctx.ellipse(0, r*1.1, r*0.86, r*0.22, 0, 0, Math.PI*2); ctx.fill();
      ctx.restore();
      ctx.fillStyle   = c.flav.colours.base;
      ctx.strokeStyle = c.flav.colours.dark;
      ctx.lineWidth = Math.max(2, r*0.06);
      ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI*2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = c.flav.colours.accent;
      ctx.beginPath(); ctx.arc(-r*0.32, -r*0.18, r*0.18, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc( r*0.22,  r*0.22, r*0.14, 0, Math.PI*2); ctx.fill();
      ctx.beginPath(); ctx.arc( r*0.30, -r*0.28, r*0.10, 0, Math.PI*2); ctx.fill();
      ctx.restore();
      continue;
    }

    const ar = sp.w / sp.h, h = g.size, w = h * ar;
    ctx.save();
    ctx.globalAlpha = c.alpha * 0.18 * g.focus;
    ctx.fillStyle = '#000';
    ctx.beginPath(); ctx.ellipse(0, h*0.42, w*0.34, h*0.08, 0, 0, Math.PI*2); ctx.fill();
    ctx.restore();
    ctx.globalAlpha = c.alpha * (1 - g.focus*0.85);
    ctx.drawImage(sp.blur, -w/2, -h/2, w, h);
    if(g.focus > 0.02){
      ctx.globalAlpha = c.alpha * g.focus;
      ctx.drawImage(sp.sharp, -w/2, -h/2, w, h);
    }
    ctx.restore();
  }
}

function drawReticle(t){
  const cx = W/2, cy = H/2, R = minDim*0.13;
  const c = activeId ? cookies.find(x => x._uid === activeId) : null;
  const ready = c && c.z <= CFG.Z_CATCH;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.lineWidth = 3;
  ctx.strokeStyle = ready ? '#fff' : 'rgba(255,255,255,.78)';
  ctx.shadowColor = 'rgba(0,0,0,.45)'; ctx.shadowBlur = 8;
  const pulse = ready ? 1 + Math.sin(t*9)*0.06 : 1;
  for(let i=0; i<4; i++){
    ctx.rotate(Math.PI/2);
    ctx.beginPath(); ctx.arc(0, 0, R*pulse, -0.35, 0.35); ctx.stroke();
  }
  ctx.shadowBlur = 0;
  if(!ready){
    ctx.beginPath(); ctx.arc(0,0,2,0,Math.PI*2);
    ctx.fillStyle='rgba(255,255,255,.85)'; ctx.fill();
  } else {
    ctx.globalAlpha = 0.9; ctx.fillStyle = '#fff';
    ctx.font = '600 15px ' + FONT; ctx.textAlign = 'center';
    ctx.fillText('TAP', 0, R*pulse + 26);
  }
  ctx.restore();
}

function drawParticles(dt){
  for(const p of particles){
    if(p.ring){
      p.life -= dt*2.2; p.r += dt*420;
      ctx.save(); ctx.globalAlpha = clamp(p.life,0,1)*0.6;
      ctx.strokeStyle = p.col; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2); ctx.stroke(); ctx.restore();
    } else {
      p.life -= dt*1.6; p.vy += 560*dt;
      p.x += p.vx*dt; p.y += p.vy*dt; p.vx *= 0.98;
      ctx.save(); ctx.globalAlpha = clamp(p.life,0,1); ctx.fillStyle = p.col;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r*clamp(p.life,0.2,1), 0, Math.PI*2);
      ctx.fill(); ctx.restore();
    }
  }
  particles = particles.filter(p => p.life > 0);
  for(const p of pops){
    p.life -= dt*0.62; p.y -= dt*52;
    const x = clamp(p.x, minDim*0.22, W - minDim*0.22);
    ctx.save();
    ctx.globalAlpha = clamp(p.life*1.4, 0, 1);
    ctx.textAlign = 'center';
    ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(0,0,0,.55)';
    ctx.fillStyle = p.col; ctx.font = '800 30px ' + FONT;
    ctx.strokeText(p.pts, x, p.y); ctx.fillText(p.pts, x, p.y);
    if(p.name){
      ctx.font = '700 15px ' + FONT; ctx.fillStyle = '#fff';
      ctx.strokeText(p.name, x, p.y + 20); ctx.fillText(p.name, x, p.y + 20);
    }
    ctx.restore();
  }
  pops = pops.filter(p => p.life > 0);
}

/* ---- tutorial demo ------------------------------------------------------- */
const easeOut     = t => 1 - Math.pow(1 - t, 3);
const easeInOut   = t => t < 0.5 ? 2*t*t : 1 - Math.pow(-2*t + 2, 2)/2;
const easeOutBack = t => { const c = 1.70158; return 1 + (c+1)*Math.pow(t-1,3) + c*Math.pow(t-1,2); };

function startTutorial(){
  hideAllScreens();
  hud.classList.remove('hidden');
  state = 'tutorial';
  tutStart = performance.now()/1000;
  tutTapDone = false;
  tutGoShown = false;
  tutLastLabel = '';
  const ready = FLAVOURS.filter(f => sprites[f.id]);
  tutFlav = ready.length ? ready[Math.floor(Math.random()*ready.length)]
                         : FLAVOURS[Math.floor(Math.random()*FLAVOURS.length)];
}

function drawDemoCookie(x, y, size, sp, flav, focused, t){
  ctx.save();
  ctx.translate(x, y);
  const bob = Math.sin(t*1.6) * size * 0.012;
  ctx.translate(0, bob);
  if(!sp){
    const r = size * 0.42;
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#000';
    ctx.beginPath(); ctx.ellipse(0, r*1.1, r*0.86, r*0.22, 0, 0, Math.PI*2); ctx.fill();
    ctx.restore();
    ctx.fillStyle   = flav.colours.base;
    ctx.strokeStyle = flav.colours.dark;
    ctx.lineWidth = Math.max(2, r*0.06);
    ctx.beginPath(); ctx.arc(0,0,r,0,Math.PI*2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = flav.colours.accent;
    ctx.beginPath(); ctx.arc(-r*0.32, -r*0.18, r*0.18, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.arc( r*0.22,  r*0.22, r*0.14, 0, Math.PI*2); ctx.fill();
  } else {
    const ar = sp.w/sp.h, h = size, w = h*ar;
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#000';
    ctx.beginPath(); ctx.ellipse(0, h*0.42, w*0.34, h*0.08, 0, 0, Math.PI*2); ctx.fill();
    ctx.restore();
    if(focused){
      ctx.drawImage(sp.sharp, -w/2, -h/2, w, h);
    } else {
      ctx.globalAlpha = 0.55;
      ctx.drawImage(sp.blur, -w/2, -h/2, w, h);
      ctx.globalAlpha = 0.75;
      ctx.drawImage(sp.sharp, -w/2, -h/2, w, h);
    }
  }
  ctx.restore();
}

function drawDemoReticle(t, ready){
  const cx = W/2, cy = H/2, R = minDim*0.13;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.lineWidth = 3;
  ctx.strokeStyle = ready ? '#fff' : 'rgba(255,255,255,.78)';
  ctx.shadowColor = 'rgba(0,0,0,.45)'; ctx.shadowBlur = 8;
  const pulse = ready ? 1 + Math.sin(t*9)*0.06 : 1;
  for(let i=0;i<4;i++){
    ctx.rotate(Math.PI/2);
    ctx.beginPath(); ctx.arc(0,0,R*pulse,-0.35,0.35); ctx.stroke();
  }
  ctx.shadowBlur = 0;
  if(ready){
    ctx.globalAlpha = 0.9; ctx.fillStyle = '#fff';
    ctx.font = '600 15px ' + FONT; ctx.textAlign = 'center';
    ctx.fillText('TAP', 0, R*pulse + 26);
  } else {
    ctx.beginPath(); ctx.arc(0,0,2,0,Math.PI*2);
    ctx.fillStyle='rgba(255,255,255,.85)'; ctx.fill();
  }
  ctx.restore();
}

function tutSpawnBurst(x, y, size){
  const base = tutFlav.colours.base, dk = tutFlav.colours.dark;
  for(let i=0;i<18;i++){
    const a = Math.random()*Math.PI*2, v = rnd(60, 260);
    particles.push({
      x, y, vx:Math.cos(a)*v, vy:Math.sin(a)*v - 60,
      life:1, r:rnd(3,8), col: Math.random()<0.5 ? base : dk
    });
  }
  particles.push({ ring:true, x, y, life:1, r:size*0.5, col:BRAND_RED });
}

function drawTutLabel(text){
  if(!text) return;
  const cx = W/2;
  const cy = H * 0.17;
  const fontSize = Math.round(clamp(W * 0.075, 28, 46));
  ctx.save();
  ctx.font = '700 ' + fontSize + 'px Caveat,cursive';
  const tw = ctx.measureText(text).width;
  const pad = 18, bh = fontSize + 20, br = 13;
  const bx = cx - tw/2 - pad, bw = tw + pad*2;
  const by = cy - bh/2;
  ctx.globalAlpha = 0.82;
  ctx.fillStyle = 'rgba(0,0,0,0.70)';
  ctx.beginPath();
  ctx.moveTo(bx+br, by);
  ctx.arcTo(bx+bw,by, bx+bw,by+bh, br);
  ctx.arcTo(bx+bw,by+bh, bx,by+bh, br);
  ctx.arcTo(bx,by+bh, bx,by, br);
  ctx.arcTo(bx,by, bx+bw,by, br);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,.72)';
  ctx.shadowBlur = 14;
  ctx.fillText(text, cx, cy);
  ctx.restore();
}

function drawTutorial(elapsed){
  const cx = W/2, cy = H/2;
  const sp = sprites[tutFlav.id];
  const startX = cx + minDim*0.30, startY = cy - minDim*0.20;
  let x, y, size, showCookie = true, focused = false, label;

  if(elapsed < TUT.T_SPOT){
    const k = elapsed / TUT.T_SPOT;
    x = startX; y = startY;
    size = minDim * 0.18 * clamp(easeOutBack(k), 0.01, 1.3);
    label = '1. Spot a cookie';
  } else if(elapsed < TUT.T_AIM){
    const k = (elapsed - TUT.T_SPOT) / (TUT.T_AIM - TUT.T_SPOT);
    const e = easeInOut(k);
    x = lerp(startX, cx, e);
    y = lerp(startY, cy, e);
    size = minDim * 0.18;
    label = '2. Centre to lock in';
  } else if(elapsed < TUT.T_FOCUS){
    const k = (elapsed - TUT.T_AIM) / (TUT.T_FOCUS - TUT.T_AIM);
    x = cx; y = cy;
    size = lerp(minDim*0.18, minDim*0.48, easeOut(k));
    focused = k > 0.45;
    label = '3. Tap to catch';
  } else if(elapsed < TUT.T_TAP){
    x = cx; y = cy;
    size = minDim*0.48;
    focused = true;
    label = '3. Tap to catch';
    if(!tutTapDone && elapsed > TUT.T_FOCUS + 0.35){
      tutTapDone = true;
      tutSpawnBurst(cx, cy, size);
    }
    if(tutTapDone) showCookie = false;
  } else {
    // "Go!" phase — draw directly on canvas so it always shows
    showCookie = false;
    label = '';
    const k = clamp((elapsed - TUT.T_TAP) / 0.35, 0, 1);
    const goSize = Math.round(minDim * 0.28);
    ctx.save();
    ctx.globalAlpha = easeOut(k);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '700 ' + goSize + 'px Caveat,cursive';
    ctx.shadowColor = 'rgba(0,0,0,.55)';
    ctx.shadowBlur = 24;
    ctx.fillText('Go!', cx, cy);
    ctx.restore();
  }

  drawTutLabel(label);
  if(showCookie) drawDemoCookie(x, y, size, sp, tutFlav, focused, elapsed);
  drawDemoReticle(elapsed, focused);
}

function endTutorial(){
  try{ DOM.tutorialLabel.classList.add('hidden'); }catch(_){}
  countEl.classList.add('hidden');
  particles = [];
  startPlay();
}

/* ---- main loop ----------------------------------------------------------- */
function frameLoop(now){
  rafId = requestAnimationFrame(frameLoop);
  const t = now / 1000;
  let dt = lastT ? t - lastT : 0; lastT = t; dt = Math.min(dt, 0.05);
  Look.update(dt);
  ctx.clearRect(0, 0, W, H);

  if(state === 'tutorial'){
    const elapsed = t - tutStart;
    if(elapsed >= TUT.TOTAL) endTutorial();
    else drawTutorial(elapsed);
  } else if(state === 'play'){
    const gt = t - gameStart;
    if(gt >= CFG.DURATION){ endGame(); }
    else {
      if(t >= nextSpawn){
        spawn();
        nextSpawn = t + rand(prng, CFG.SPAWN_MIN, CFG.SPAWN_MAX);
      }
      updateCookies(t, dt);
      if(gt < 3.0 && cookies.length === 0 && totalCaught === 0){
        const fade = clamp(1 - gt/3.0, 0, 1) * clamp(gt/0.4, 0, 1);
        const msg = 'Look around for cookies!';
        ctx.save();
        ctx.font = '700 22px Caveat,cursive';
        const tw = ctx.measureText(msg).width;
        const cx2 = W/2, pad = 14, bh = 38, br = 10;
        const bx = cx2 - tw/2 - pad, by = H/2 + minDim*0.22;
        ctx.globalAlpha = 0.75 * fade;
        ctx.fillStyle = 'rgba(0,0,0,.65)';
        ctx.beginPath();
        ctx.moveTo(bx+br,by); ctx.arcTo(bx+tw+pad*2,by, bx+tw+pad*2,by+bh, br);
        ctx.arcTo(bx+tw+pad*2,by+bh, bx,by+bh, br); ctx.arcTo(bx,by+bh, bx,by, br);
        ctx.arcTo(bx,by, bx+tw+pad*2,by, br); ctx.closePath(); ctx.fill();
        ctx.globalAlpha = fade;
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,.4)'; ctx.shadowBlur = 6;
        ctx.fillText(msg, cx2, by + bh/2);
        ctx.restore();
      }
      const left = Math.max(0, CFG.DURATION - gt);
      timerFill.style.transform = 'scaleX(' + (left / CFG.DURATION) + ')';
      scoreEl.textContent = score;
      liveEl.textContent  = cookies.length;
      if(left <= 45){
        clockEl.classList.remove('hidden');
        const m = Math.floor(left/60), s = Math.floor(left%60);
        clockEl.textContent = m + ':' + String(s).padStart(2, '0');
        clockEl.classList.toggle('urgent', left <= 10);
      } else if(!clockEl.classList.contains('hidden')){
        clockEl.classList.add('hidden');
      }
    }
  }
  drawCookies(t);
  drawParticles(dt);
  if(state === 'play') drawReticle(t);
}

/* ---- flow ---------------------------------------------------------------- */
function hideAllScreens(){
  for(const k in screens) screens[k].classList.add('hidden');
}
function show(name){
  if(!(name in screens)){
    console.warn('show(): unknown screen "' + name + '"');
    hideAllScreens();
    return;
  }
  for(const k in screens) screens[k].classList.toggle('hidden', k !== name);
}
function toast(msg){
  toastEl.textContent = msg;
  toastEl.classList.add('show');
  setTimeout(() => toastEl.classList.remove('show'), 3200);
}

function waitForGyro(ms){
  return new Promise(resolve => {
    if(Look.usingGyro()) return resolve(true);
    const t0 = performance.now();
    (function poll(){
      if(Look.usingGyro()) return resolve(true);
      if(performance.now() - t0 > ms) return resolve(false);
      requestAnimationFrame(poll);
    })();
  });
}

function showUnsupported(noCam, noGyro){
  stopCamera();
  video.style.display = 'none';
  hud.classList.add('hidden');
  let msg;
  if(noCam && noGyro)
    msg = "The Ben's Cookie Hunt needs a phone with a camera and motion sensors. Scan the in-store QR code on your phone to play.";
  else if(noCam)
    msg = "We couldn't access the camera. Allow camera access, or open this on your phone via the in-store QR code.";
  else
    msg = "We couldn't detect motion sensors — this is a phone experience. Open it on your phone via the in-store QR code and allow motion access.";
  DOM.unsupportedMsg.textContent = msg;
  state = 'start';
  show('unsupported');
}

async function begin(){
  unlockAudio();
  Look.init();
  const sensorsP = Look.requestPermission();
  let camOk = true;
  try{ await startCamera('environment'); }
  catch(err){ camOk = false; }
  const granted = await sensorsP;
  const haveGyro = granted ? await waitForGyro(CFG.GYRO_WAIT_MS) : false;

  if(!camOk || !haveGyro){ showUnsupported(!camOk, !haveGyro); return; }

  startTutorial();
}

function startPlay(){
  score = 0; totalCaught = 0; tally = {};
  cookies = []; particles = []; pops = [];
  clockEl.classList.add('hidden'); clockEl.classList.remove('urgent');
  gameStart = performance.now() / 1000;
  nextSpawn = gameStart + 0.3;
  state = 'play';
}

function topFlavour(){
  let best = null, bc = -1;
  for(const f of FLAVOURS){
    const c = tally[f.id] || 0;
    if(c > bc){ bc = c; best = f; }
  }
  return bc > 0 ? best : null;
}

function endGame(){
  state = 'over';
  stopCamera();
  video.style.display = 'none';
  hud.classList.add('hidden'); clockEl.classList.add('hidden');
  cookies = []; particles = []; pops = []; activeId = null;
  const top = topFlavour();
  DOM.statTotal.textContent = totalCaught;
  DOM.statScore.textContent = score;
  const hero = DOM.hero; hero.innerHTML = '';
  if(top){
    const im = new Image(); im.src = assetUrl(top.sprite); hero.appendChild(im);
    DOM.heroName.textContent = top.name;
    DOM.heroSub.textContent  = 'your top flavour';
  } else {
    DOM.heroName.textContent = 'No cookies caught';
    DOM.heroSub.textContent  = 'give it another go';
  }
  const bd = DOM.breakdown; bd.innerHTML = '';
  FLAVOURS.filter(f => tally[f.id])
          .sort((a,b) => tally[b.id] - tally[a.id])
          .forEach(f => {
    const row = document.createElement('div');
    row.className = 'bd-row';
    row.innerHTML = '<img src="' + assetUrl(f.sprite) + '">' +
                    '<span>' + f.name + '</span>' +
                    '<b>' + tally[f.id] + '</b>';
    bd.appendChild(row);
  });
  show('over');
  selfieFlav = top;
}

/* ---- selfie / share ------------------------------------------------------ */
async function openSelfie(){
  if(!selfieFlav){ toast('Catch a cookie first!'); return; }
  show('selfie'); state = 'selfie';
  try{ await startCamera('user'); }
  catch(_){ toast('Front camera unavailable'); }
  sizeSelfie();
  requestAnimationFrame(selfieLoop);
}
function sizeSelfie(){
  const box = selfCanvas.parentElement.getBoundingClientRect();
  const w = box.width;
  const h = Math.min(box.width * 1.25, window.innerHeight * 0.62);
  selfCanvas.style.width = w + 'px';
  selfCanvas.style.height = h + 'px';
  selfCanvas.width  = Math.round(w * DPR);
  selfCanvas.height = Math.round(h * DPR);
}
function selfieLoop(){
  if(state !== 'selfie') return;
  requestAnimationFrame(selfieLoop);
  drawSelfie(selfCtx, selfCanvas.width, selfCanvas.height, true);
}

function roundRectPath(c, x, y, w, h, r){
  c.beginPath();
  c.moveTo(x+r, y);
  c.arcTo(x+w, y,   x+w, y+h, r);
  c.arcTo(x+w, y+h, x,   y+h, r);
  c.arcTo(x,   y+h, x,   y,   r);
  c.arcTo(x,   y,   x+w, y,   r);
  c.closePath();
}
function drawSticker(c, img, cxRel, cyRel, wRel, angleDeg, cw, ch){
  if(!img) return;
  const w = cw * wRel;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const h = w * (ih / iw);
  c.save();
  c.translate(cxRel * cw, cyRel * ch);
  c.rotate(angleDeg * Math.PI/180);
  c.drawImage(img, -w/2, -h/2, w, h);
  c.restore();
}
function drawSelfie(c, cw, ch, mirror){
  c.save();
  c.clearRect(0,0,cw,ch);
  c.fillStyle = '#fff'; c.fillRect(0,0,cw,ch);

  const inset = Math.min(cw,ch)*0.045;
  const cardX = inset, cardY = inset;
  const cardW = cw - 2*inset;
  const cardH = ch - 2*inset - 78*DPR;
  c.save();
  const r = Math.min(cw,ch)*0.05;
  roundRectPath(c, cardX, cardY, cardW, cardH, r); c.clip();
  if(video.videoWidth){
    const vr = video.videoWidth/video.videoHeight, cr = cardW/cardH;
    let dw, dh;
    if(vr > cr){ dh = cardH; dw = cardH*vr; }
    else       { dw = cardW; dh = cardW/vr; }
    c.save();
    if(mirror){
      c.translate(cardX + cardW, 0); c.scale(-1, 1);
      c.drawImage(video, (cardW - dw)/2, cardY + (cardH - dh)/2, dw, dh);
    } else {
      c.drawImage(video, cardX + (cardW - dw)/2, cardY + (cardH - dh)/2, dw, dh);
    }
    c.restore();
  } else {
    c.fillStyle = '#2a2018'; c.fillRect(cardX, cardY, cardW, cardH);
  }
  c.restore();

  c.save();
  c.strokeStyle = BRAND_RED; c.lineWidth = 4*DPR;
  roundRectPath(c, cardX, cardY, cardW, cardH, r); c.stroke();
  c.restore();

  drawSticker(c, frame.clapper, 0.16, 0.10, 0.22, -12, cw, ch);
  drawSticker(c, frame.heart,   0.86, 0.10, 0.16,  14, cw, ch);
  drawSticker(c, frame.bag,     0.13, 0.74, 0.22,  -8, cw, ch);
  drawSticker(c, frame.crumbs,  0.66, 0.82, 0.42,   3, cw, ch);

  const sp = selfieFlav && sprites[selfieFlav.id];
  if(sp){
    const h = ch*0.36*selfiePos.s, w = h*(sp.w/sp.h);
    c.drawImage(sp.sharp, selfiePos.x*cw - w/2, selfiePos.y*ch - h/2, w, h);
  }

  const stripH = 78*DPR;
  c.fillStyle = BRAND_RED; c.fillRect(0, ch - stripH, cw, stripH);
  c.fillStyle = '#fff'; c.textAlign = 'center';
  c.font = '700 ' + (22*DPR) + 'px ' + FONT;
  c.fillText("I caught " + totalCaught + " cookies", cw/2, ch - stripH + 30*DPR);
  c.font = '600 ' + (16*DPR) + 'px ' + FONT;
  c.fillStyle = 'rgba(255,255,255,.92)';
  c.fillText('My top cookie: ' + (selfieFlav ? selfieFlav.name : ''), cw/2, ch - 22*DPR);

  c.restore();
}

async function captureShare(){
  const out = document.createElement('canvas');
  out.width  = selfCanvas.width;
  out.height = selfCanvas.height;
  drawSelfie(out.getContext('2d'), out.width, out.height, true);
  const blob = await new Promise(r => out.toBlob(r, 'image/png'));
  if(!blob){ toast('Could not build image'); return; }
  const file = new File([blob], 'cookie-catch.png', { type:'image/png' });
  const data = {
    files:[file], title:"The Ben's Cookie Hunt",
    text: "I caught " + totalCaught + " cookies. Come try the cookies in-store! @benscookies_in"
  };
  if(navigator.canShare && navigator.canShare({ files:[file] })){
    try{ await navigator.share(data); return; }
    catch(e){ if(e.name === 'AbortError') return; }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'cookie-catch.png'; a.click();
  URL.revokeObjectURL(url);
  toast('Saved! Open Instagram to share your selfie.');
}

/* ---- input wiring -------------------------------------------------------- */
function placeSelfie(e){
  const b = selfCanvas.getBoundingClientRect();
  selfiePos.x = clamp((e.clientX - b.left) / b.width,  0.1, 0.9);
  selfiePos.y = clamp((e.clientY - b.top ) / b.height, 0.1, 0.9);
}
function bindInput(){
  const stage = DOM.stage;
  let downX = 0, downY = 0, moved = false;
  stage.addEventListener('pointerdown', e => { downX = e.clientX; downY = e.clientY; moved = false; });
  stage.addEventListener('pointermove', e => {
    if(Math.hypot(e.clientX - downX, e.clientY - downY) > 8) moved = true;
  });
  window.addEventListener('pointerup', () => {
    if(moved) return;
    if(state === 'play') tryCatch();
    else if(state === 'tutorial') endTutorial();
  });

  selfCanvas.addEventListener('pointerdown', e => { selfDragging = true;  placeSelfie(e); });
  selfCanvas.addEventListener('pointermove', e => { if(selfDragging) placeSelfie(e); });
  window.addEventListener('pointerup', () => { selfDragging = false; });

  DOM.startBtn.addEventListener('click', begin);
  DOM.selfieBtn.addEventListener('click', openSelfie);
  DOM.againBtn.addEventListener('click', () => location.reload());
  DOM.captureBtn.addEventListener('click', captureShare);
  DOM.backBtn.addEventListener('click', () => { stopCamera(); state = 'over'; show('over'); });
  // DOM.selfieScale.addEventListener('input', e => { selfiePos.s = parseFloat(e.target.value); });
  DOM.retryBtn.addEventListener('click', () => location.reload());

  window.addEventListener('resize', () => { resize(); if(state === 'selfie') sizeSelfie(); });
  if(window.visualViewport) window.visualViewport.addEventListener('resize', resize);
}

/* ---- boot ---------------------------------------------------------------- */
async function boot(){
  DOM.init();
  resize();
  bindInput();
  if(document.fonts && document.fonts.ready){
    try{ await document.fonts.ready; }catch(_){}
  }
  await Promise.all([loadSprites(), loadFrame()]);
  const sc = DOM.startCookie;
  if(sc && FLAVOURS.length){
    const f = FLAVOURS[Math.floor(Math.random() * FLAVOURS.length)];
    sc.src = assetUrl(f.sprite);
  }
  rafId = requestAnimationFrame(frameLoop);
}

// Guard: deferred scripts run before DOMContentLoaded fires, but on Shopify
// liquid-injected sections DOMContentLoaded may have already fired.
if(document.readyState === 'loading'){
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

})();
